import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { postTrackingEvent, buildTrackingPayload } from "@/lib/tracking";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { CheckCircle2, PhoneCall } from "lucide-react";

const PHONE = "954-827-9793";
const PHONE_TEL = "+19548279793";

const faqs = [
  {
    q: 'Will I be unconscious or "out of it"?',
    a: "No. SPRAVATO® isn't anesthesia. Many people experience dissociation — a temporary sense of feeling disconnected from themselves or their surroundings — during and shortly after treatment. It's one of the most common effects, it typically begins soon after dosing, and it generally resolves the same day. Our team is with you throughout.",
  },
  {
    q: "Can I drive myself home?",
    a: "No. You'll need to arrange a ride home from a trusted person. Rideshare services are not sufficient — a responsible adult who can accompany you is required.",
  },
  {
    q: "Is this the same as ketamine infusions?",
    a: "They're related but not the same. SPRAVATO® (esketamine) is an FDA-approved nasal spray with specific approved indications, given in a certified setting under a federal REMS program, and it's covered by many insurance plans. IV ketamine is administered as an infusion and is used off-label for depression. We offer both, and we'll talk through which makes more sense for your situation at your consultation.",
  },
  {
    q: "Do I have to stop my current antidepressant?",
    a: "Not necessarily. SPRAVATO® is used with or without an oral antidepressant for treatment-resistant depression, and with an oral antidepressant for MDD with suicidal thoughts or actions. Your provider will advise you.",
  },
  {
    q: "How soon might I notice a difference?",
    a: "That varies from person to person, and it's something to discuss with your provider at your consultation.",
  },
  {
    q: "Do I need a referral?",
    a: "No referral is required. You can contact us directly to request a consultation.",
  },
  {
    q: "Where do I park?",
    a: "Free on-site parking is available at our Coral Springs office. Enter the building and follow the signs to Suite B.",
  },
  {
    q: "How do I get started?",
    a: "Request a consultation or call 954-827-9793. Your first visit is an evaluation — we'll review your history and treatment goals and determine together whether SPRAVATO® is appropriate for you.",
  },
];

export const faqsForSchema = faqs;

export const SpravatoFaqSection = () => (
  <section className="relative bg-gradient-hero py-16 lg:py-28">
    <div className="container-px mx-auto max-w-3xl">
      <div className="text-center">
        <span className="text-sm font-semibold uppercase tracking-widest text-accent">
          Questions, answered.
        </span>
        <h2 className="mt-3 font-headings text-2xl font-bold text-primary sm:text-3xl lg:text-[2.5rem]">
          Questions, answered.
        </h2>
      </div>
      <Accordion
        type="single"
        collapsible
        className="mt-10 space-y-3 sm:mt-12 sm:space-y-4"
      >
        {faqs.map((faq, i) => (
          <AccordionItem
            key={i}
            value={`item-${i}`}
            className="rounded-xl border border-border bg-card px-5 shadow-soft data-[state=open]:border-accent/40 data-[state=open]:shadow-premium"
          >
            <AccordionTrigger className="text-left font-headings text-base font-semibold text-primary hover:no-underline">
              {faq.q}
            </AccordionTrigger>
            <AccordionContent className="text-[15px] leading-relaxed text-muted-foreground">
              {faq.a}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  </section>
);

export const SpravatoConsultSection = () => {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [triedMeds, setTriedMeds] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const source = "spravato-consult";
    const formName = "SPRAVATO Consultation Request";
    const payload = buildTrackingPayload(source, formName);
    payload.formData = {
      first_name: name,
      phone,
      email,
    };
    payload.formLabels = {
      first_name: "Name",
      phone: "Phone",
      email: "Email",
    };

    setSubmitting(true);
    try {
      await postTrackingEvent(payload, {
        customFields: {
          Vk8gVzfvpfaSxmKuryUm: {
            value: triedMeds,
            label: "Tried two or more antidepressants without relief",
          },
        },
      });
    } catch {
      /* fire-and-forget — never block the UX */
    }
    setSubmitting(false);
    setSubmitted(true);
  };

  return (
    <section
      id="consult"
      className="relative overflow-hidden bg-gradient-navy py-16 text-primary-foreground lg:py-28"
    >
      <div className="pointer-events-none absolute -right-24 top-0 h-72 w-72 rounded-full bg-accent/10 blur-3xl" />
      <div className="pointer-events-none absolute -left-24 bottom-0 h-64 w-64 rounded-full bg-medium-blue/20 blur-3xl" />
      <div className="container-px relative mx-auto grid max-w-7xl items-center gap-10 sm:gap-12 lg:grid-cols-2">
        <div>
          <span className="text-sm font-semibold uppercase tracking-widest text-accent">
            Your first step is a conversation.
          </span>
          <h2 className="mt-3 font-headings text-2xl font-bold sm:text-3xl md:text-4xl lg:text-[2.5rem]">
            No commitment, and no treatment on day one. Just an evaluation with
            our team to find out whether SPRAVATO® is a fit for you.
          </h2>
          <div className="mt-8 flex flex-col items-start gap-4 sm:flex-row sm:items-center">
            <Button
              asChild
              size="lg"
              className="w-full sm:w-auto bg-gradient-accent text-accent-foreground shadow-glow hover:opacity-90"
            >
              <a href="#consult-form">Request a Consultation</a>
            </Button>
            <a
              href={`tel:${PHONE_TEL}`}
              className="inline-flex items-center gap-2 font-semibold text-white underline decoration-accent/50 underline-offset-4 hover:decoration-accent"
            >
              <PhoneCall className="h-5 w-5 text-accent" />
              Call {PHONE}
            </a>
          </div>
          <ul className="mt-8 space-y-4">
            {[
              "No commitment, no treatment on day one",
              "We review your history and what you've tried",
              "If it isn't the right fit, we'll tell you that",
            ].map((item) => (
              <li key={item} className="flex items-center gap-3">
                <CheckCircle2 className="h-5 w-5 text-accent" />
                <span className="text-primary-foreground/90">{item}</span>
              </li>
            ))}
          </ul>
        </div>

        <div
          id="consult-form"
          className="rounded-2xl bg-card p-5 text-card-foreground shadow-2xl sm:p-7 lg:p-9"
        >
          {submitted ? (
            <div className="flex flex-col items-center py-12 text-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-accent/15">
                <CheckCircle2 className="h-9 w-9 text-accent" />
              </div>
              <h3 className="mt-5 font-headings text-2xl font-bold text-primary">
                Thank you — we'll be in touch
              </h3>
              <p className="mt-3 max-w-sm text-muted-foreground">
                A Neuro Wellness specialist will contact you within one business
                day. We're glad you're taking this step.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <h3 className="font-headings text-xl font-bold text-primary">
                Request a consultation
              </h3>
              <div className="space-y-2">
                <Label htmlFor="s-name">Name</Label>
                <Input
                  id="s-name"
                  name="name"
                  type="text"
                  required
                  autoComplete="name"
                  placeholder="Your full name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              <div className="grid gap-5 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="s-phone">Phone</Label>
                  <Input
                    id="s-phone"
                    name="phone"
                    type="tel"
                    required
                    autoComplete="tel"
                    placeholder="(555) 000-0000"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="s-email">Email</Label>
                  <Input
                    id="s-email"
                    name="email"
                    type="email"
                    required
                    autoComplete="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="s-eligibility">
                  Have you tried two or more antidepressants without adequate
                  relief?
                </Label>
                <Select value={triedMeds} onValueChange={setTriedMeds} required>
                  <SelectTrigger id="s-eligibility">
                    <SelectValue placeholder="Select an answer" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Yes">Yes</SelectItem>
                    <SelectItem value="No">No</SelectItem>
                    <SelectItem value="Not sure">Not sure</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button
                type="submit"
                size="lg"
                disabled={submitting}
                className="w-full bg-gradient-accent text-accent-foreground shadow-glow hover:opacity-90"
              >
                {submitting ? "Sending…" : "Request a Consultation"}
              </Button>
              <p className="text-center text-xs text-muted-foreground">
                This form is not for medical emergencies. If you're in crisis,
                call or text{" "}
                <a
                  href="tel:988"
                  className="font-semibold text-primary underline"
                >
                  988
                </a>{" "}
                or go to your nearest emergency room.
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};
